// @ts-strict-ignore
export function debounce(func: any, timeout = 300) {
  let timer: NodeJS.Timeout
  return (...args: any) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, timeout)
  }
}

/**
 * timeout
 *
 * Used when we want to wait for a tracking event to resolve,
 * and we don't want to block what happens after
 *
 * @param {Promise} promise - promise which should resolve
 * @param {number} [ms] - (optional) number of ms before promise resolves
 * @returns {Promise} - which will resolve after a timeout
 */
export function timeout(promise, ms = 50) {
  return Promise.race([willResolve(ms), promise])
}

function willResolve(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * Takes an object slice from the schema, along with the dataset, and returns the absolute value,
 * either from the dataset or the schema.
 *
 * @param {Object} slice
 * @param {*] [slice.value] - The absolute value.
 * @param {string} [slice.valueFrom] - The path to the value on the dataset.
 * @param {Object} dataset - The dataset to lookup.
 * @returns {Value}
 */
export const getValue = (slice: Slice, dataset: any) => {
  const [type, value] = getValueType(slice)
  if (type === 'valueFrom') {
    return getValueFromKey(value, dataset)
  }

  return value
}

/**
 * Takes an key from the schema, along with the dataset, and returns the absolute value
 *
 * @param {*] [key] - The key to lookup value for.
 * @param {Object} dataset - The dataset to lookup.
 * @returns {Value}
 */
function getValueFromKey(key: string, dataset: any) {
  const valueFromData = path(key.split('.'), dataset)
  return Array.isArray(valueFromData) ? valueFromData[0] : valueFromData
}

function path(arr, obj, def = undefined) {
  return arr.reduce(
    (acc, it) => (acc !== def && {}.hasOwnProperty.call(acc, it) ? acc[it] : def),
    obj,
  )
}

interface Slice {
  value?: string
  valueFrom?: string
  validIf?: string
}

/**
 * Takes an object slice from the schema, and finds whether the slice is a 'value' or 'valueFrom'
 * type.
 *
 * @param {Object} slice
 * @param {*] [slice.value] - The absolute value.
 * @param {string} [slice.valueFrom] - The path to the value on the dataset.
 * @returns {[ValueType, Value]}
 */
function getValueType(slice: Slice): [any, any] {
  if (!isObject(slice)) {
    return [null, null]
  }

  if ({}.hasOwnProperty.call(slice, 'valueFrom')) {
    return ['valueFrom', slice.valueFrom]
  }

  if ({}.hasOwnProperty.call(slice, 'value')) {
    return ['value', slice.value]
  }

  return [null, null]
}

function isObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
